import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Home, QrCode, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getParty, getQueue, removeFromQueue, reorderQueue, resetQueue, updateQueueItemStatus, getMember } from '../services/api';
import ActionsMenu from '../components/ActionsMenu';
import Playlist from '../components/Playlist';
import SongSearch from '../components/SongSearch';
import CustomizationPanel from '../components/CustomizationPanel';
import ThemePicker from '../components/ThemePicker';
import QRCodeModal from '../components/QRCodeModal';
import useSSMusicJobs from '../hooks/useSSMusicJobs';
import './GuestPage.css';

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || window.location.origin;

export default function GuestPage({ signOutAction, accountInfoAction }) {
  const { partyId, memberId: memberIdFromUrl } = useParams();
  const navigate = useNavigate();
  const { authLevel } = useAuth();

  const [party, setParty] = useState(null);
  const [queue, setQueue] = useState([]);
  const { sendToSSMusic, ssmusicState } = useSSMusicJobs(partyId, setQueue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [isMobile, setIsMobile] = useState(() => window.matchMedia?.('(max-width: 768px)').matches ?? false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  const [statusPanelOpen, setStatusPanelOpen] = useState(false);
  const [audioPanelOpen, setAudioPanelOpen] = useState(false);
  const [qrPanelOpen, setQrPanelOpen] = useState(false);
  const [videoProgress, setVideoProgress] = useState({ currentTime: 0, duration: 0 });
  const [connected, setConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [settings, setSettings] = useState({ key: 0, tempo: 1.0, vocalLevel: 100 });

  const socketRef = useRef(null);
  const qrDialogRef = useRef(null);
  const latestProgressRef = useRef({ currentTime: 0, duration: 0 });

  const memberName = sessionStorage.getItem('memberName') || 'Guest';
  const memberId = memberIdFromUrl || sessionStorage.getItem('memberId');
  const member = { id: memberId, name: memberName };
  const queueVisible = pageVisible && (!isMobile || activeTab === 'queue');

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(max-width: 768px)');
    const onResize = () => setIsMobile(mediaQuery.matches);
    const onVisibility = () => setPageVisible(document.visibilityState !== 'hidden');
    if (mediaQuery?.addEventListener) mediaQuery.addEventListener('change', onResize);
    else mediaQuery?.addListener(onResize);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener('change', onResize);
      else mediaQuery?.removeListener(onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (qrPanelOpen) qrDialogRef.current?.showModal();
    else qrDialogRef.current?.close();
  }, [qrPanelOpen]);

  const handleRemove = async (itemId) => {
    try {
      await removeFromQueue(partyId, itemId);
      setQueue((current) => current.filter((item) => item.id !== itemId));
      setError('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove song.');
    }
  };

  const handleSongAdded = useCallback(async () => {
    try {
      const res = await getQueue(partyId);
      setQueue(res.data);
    } catch (err) {
      console.error('Failed to refresh queue after adding song:', err);
      // socket will handle the update if this fails
    }
  }, [partyId]);

  const handleResetQueue = useCallback(async () => {
    if (!window.confirm('Reset all songs (except the currently playing one) back to queued?')) return;
    try {
      await resetQueue(partyId);
      setError('');
    } catch {
      setError('Failed to reset queue.');
    }
  }, [partyId]);

  const handlePlay = useCallback(
    async (item) => {
      try {
        await updateQueueItemStatus(partyId, item.id, 'playing');
      } catch {
        setError('Failed to start song.');
      }
    },
    [partyId]
  );

  const handlePause = useCallback(
    async (item) => {
      try {
        await updateQueueItemStatus(partyId, item.id, 'paused');
      } catch {
        setError('Failed to pause song.');
      }
    },
    [partyId]
  );

  const handleReorder = async (reordered) => {
    const previous = queue;
    setQueue(reordered);
    try {
      await reorderQueue(partyId, reordered.map((item) => ({ id: item.id, position: item.position })));
    } catch {
      setQueue(previous);
      setError('Failed to reorder queue.');
    }
  };

  // Reset video progress when the active song changes (playing or paused)
  const activeItemId = queue.find((i) => i.status === 'playing' || i.status === 'paused')?.id;
  useEffect(() => {
    latestProgressRef.current = { currentTime: 0, duration: 0 };
    setVideoProgress({ currentTime: 0, duration: 0 });
  }, [activeItemId]);

  // Flush latest progress to state at a reduced rate to limit re-renders on
  // low-RAM devices. The socket still receives events at full frequency; only
  // the React state update (which triggers a full component re-render) is
  // throttled here.
  useEffect(() => {
    if (!queueVisible) return;
    const flushProgress = () => {
      setVideoProgress((prev) => {
        const next = latestProgressRef.current;
        if (prev.currentTime === next.currentTime && prev.duration === next.duration) {
          return prev; // bail out of re-render when nothing changed
        }
        return { ...next };
      });
    };
    flushProgress();
    const id = setInterval(flushProgress, 2000);
    return () => clearInterval(id);
  }, [queueVisible]);

  // Send a seek request to the organizer's player
  const handleSeek = useCallback(
    (seekTime) => {
      socketRef.current?.emit('video:seek', { partyId, seekTime });
    },
    [partyId]
  );

  // Send audio settings change to the organizer's player
  const handleSettingsChange = useCallback(
    (newSettings) => {
      setSettings(newSettings);
      socketRef.current?.emit('audio:settings', { partyId, settings: newSettings });
    },
    [partyId]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const [partyRes, queueRes] = await Promise.all([
          getParty(partyId),
          getQueue(partyId),
        ]);

        // Validate that the member in the URL actually belongs to this party
        if (memberIdFromUrl) {
          try {
            await getMember(partyId, memberIdFromUrl);
          } catch (memberErr) {
            if ([401, 403, 404].includes(memberErr.response?.status)) {
              window.dispatchEvent(new Event('auth:expired'));
              navigate('/');
              return;
            }
          }
        }

        setParty(partyRes.data);
        setQueue(queueRes.data);
        setIsLocked(partyRes.data.is_locked || false);
        if (partyRes.data.is_locked) {
          setActiveTab('queue');
        }
      } catch {
        setError('Failed to load party. It may have ended.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [partyId, memberIdFromUrl, navigate]);

  // Socket for real-time updates
  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'],
      auth: (callback) => callback({ token: sessionStorage.getItem('authToken') }) });
    socketRef.current = socket;
    let hasConnected = false;
    socket.on('connect', () => {
      setConnected(true);
      setSocketId(socket.id);
      setReconnectAttempts(0);
      socket.emit('join:party', partyId, (result) => { if (result?.error) setError(result.error); });
      if (hasConnected) {
        getQueue(partyId).then(({ data }) => {
          if (socketRef.current === socket) setQueue(data);
        }).catch(() => {});
      }
      hasConnected = true;
    });
    socket.on('connect_error', (failure) => {
      if (failure.message === 'Unauthorized') window.dispatchEvent(new Event('auth:expired'));
    });
    socket.on('disconnect', (reason) => {
      setConnected(false);
      setSocketId(null);
      if (reason === 'io server disconnect') getQueue(partyId).catch(() => setError('Party access ended.'));
    });
    socket.on('reconnect_attempt', (attempt) => {
      setReconnectAttempts(attempt);
    });
    socket.on('queue:update', ({ queue: updatedQueue }) => {
      if (updatedQueue) {
        setQueue(updatedQueue);
      }
    });
    socket.on('party:lock', ({ is_locked }) => {
      setIsLocked(is_locked);
      if (is_locked) {
        setActiveTab('queue');
      }
    });
    // Receive real-time playback position broadcast by the organizer's player.
    // Store in a ref; a separate interval flushes to state at a reduced rate
    // to limit re-renders on low-RAM devices.
    socket.on('video:progress', ({ currentTime, duration }) => {
      latestProgressRef.current = { currentTime, duration };
    });
    return () => {
      socket.emit('leave:party', partyId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [partyId]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading party…</p>
      </div>
    );
  }

  if (!party) {
    return (
      <div className="error-screen">
        <p>{error || 'Party not found.'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const upcomingCount = queue.filter((i) => i.status !== 'played').length;
  const connectionState = connected ? 'connected' : reconnectAttempts > 0 ? 'reconnecting' : 'disconnected';

  return (
    <div className="guest-layout">
      {/* Header */}
      <header className="guest-header">
        <div className="guest-header-info">
          <div className="guest-header-title">
            {authLevel !== 'guest' && authLevel !== 'none' && (
              <button
                type="button"
                className="home-button"
                onClick={() => navigate('/')}
                aria-label="Home"
                title="Home"
              >
                <Home size={20} aria-hidden="true" />
              </button>
            )}
            <span className="party-badge">🎉 {party.name}</span>
          </div>
          {isLocked && <span className="locked-badge">🔒 Locked</span>}
          <span className="member-badge">👤 {memberName}</span>
        </div>
        <div className="guest-header-actions">
          <ThemePicker />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setQrPanelOpen(true)}
            aria-label="Show party QR code"
            title="Share party invitation"
          >
            <QrCode size={20} aria-hidden="true" />
          </button>
          <button
            className="btn-audio-toggle"
            onClick={() => setAudioPanelOpen((o) => !o)}
            aria-label="Toggle audio settings"
          >
            🎛️ Audio
          </button>
          <button
            className="btn-status-toggle"
            onClick={() => setStatusPanelOpen((o) => !o)}
            aria-label="Toggle connection status"
          >
            <span className={`connection-dot connection-dot--${connectionState}`} />
            Status
          </button>
          <ActionsMenu label="Account actions" actions={[
            accountInfoAction,
            { label: 'Reset All', icon: RotateCcw, disabled: queue.length === 0, run: handleResetQueue },
            signOutAction && { ...signOutAction, separated: true },
          ]} />
        </div>
      </header>

      <dialog ref={qrDialogRef} className="guest-qr-dialog"
        aria-label="Share party invitation" onClose={() => setQrPanelOpen(false)}
        onCancel={() => setQrPanelOpen(false)} onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setQrPanelOpen(false);
          }
        }}>
        <div className="guest-qr-dialog-actions">
          <button type="button" className="btn btn-ghost" aria-label="Close party QR code"
            title="Close" onClick={() => setQrPanelOpen(false)}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {qrPanelOpen && <QRCodeModal party={party} shareOnly />}
      </dialog>

      {/* Mobile tab switcher */}
      <nav className="mobile-tabs">
        {!isLocked && (
          <button
            className={`mobile-tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            🔍 Search
          </button>
        )}
        <button
          className={`mobile-tab ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          🎵 Queue
          {upcomingCount > 0 && <span className="badge">{upcomingCount}</span>}
        </button>
        <button
          className={`mobile-tab ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          <span className={`connection-dot connection-dot--${connectionState}`} />
          Status
        </button>
      </nav>

      {/* Content */}
      <main className="guest-main">
        {error && <div className="error-msg">{error}</div>}

        <div className="guest-columns">
          {/* Search section */}
          {!isLocked && (
          <div className={`search-section${activeTab !== 'search' ? ' mobile-hidden' : ''}`}>
            <div className="section-heading">Search for songs to add</div>
            <SongSearch
              partyId={partyId}
              member={member}
              onAdded={handleSongAdded}
            />
          </div>
          )}

          {/* Queue section */}
          <div className={`queue-section${activeTab !== 'queue' ? ' mobile-hidden' : ''}`}>
            <div className="section-heading">
              🎵 Queue
              {upcomingCount > 0 && <span className="badge">{upcomingCount}</span>}
            </div>
            {queue.length === 0 ? (
              <div className="empty-state">
                <span>🎵</span>
                <p>No songs yet!</p>
                {!isLocked && <p className="muted">Use the search panel to find and add songs.</p>}
              </div>
            ) : (
              <Playlist
                queue={queue}
                onRemove={handleRemove}
                onReorder={handleReorder}
                onPlay={handlePlay}
                onPause={handlePause}
                onSendToSSMusic={isLocked ? undefined : sendToSSMusic}
                ssmusicState={ssmusicState}
                isOrganizer={false}
                canReorder={true}
                canRemove={true}
                currentTime={videoProgress.currentTime}
                duration={videoProgress.duration}
                onSeek={handleSeek}
              />
            )}
          </div>
        </div>
      </main>

      {/* Audio settings slideout */}
      {audioPanelOpen && (
        <div
          className="audio-overlay"
          onClick={() => setAudioPanelOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setAudioPanelOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close audio settings"
        />
      )}
      <div className={`audio-panel${audioPanelOpen ? ' open' : ''}`}>
        <div className="audio-panel-header">
          <span className="audio-panel-title">🎛️ Audio Settings</span>
          <button
            className="audio-panel-close"
            onClick={() => setAudioPanelOpen(false)}
            aria-label="Close audio settings"
          >
            ✕
          </button>
        </div>
        <div className="audio-panel-body">
          <CustomizationPanel settings={settings} onChange={handleSettingsChange} />
        </div>
      </div>

      {/* Connection status slideout */}
      {statusPanelOpen && (
        <div
          className="status-overlay"
          onClick={() => setStatusPanelOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setStatusPanelOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close connection status"
        />
      )}
      <div className={`status-panel${statusPanelOpen ? ' open' : ''}`}>
        <div className="status-panel-header">
          <span className="status-panel-title">📶 Connection Status</span>
          <button
            className="status-panel-close"
            onClick={() => setStatusPanelOpen(false)}
            aria-label="Close connection status"
          >
            ✕
          </button>
        </div>
        <div className="status-panel-body">
          <div className="status-card">
            <div className="status-row">
              <span className="status-label">State</span>
              <span className={`status-value connection-status connection-status--${connectionState}`}>
                <span className={`connection-dot connection-dot--${connectionState}`} />
                {connected ? 'Connected' : reconnectAttempts > 0 ? `Reconnecting… (attempt ${reconnectAttempts})` : 'Disconnected'}
              </span>
            </div>
            {socketId && (
              <div className="status-row">
                <span className="status-label">Socket ID</span>
                <span className="status-value status-mono">{socketId}</span>
              </div>
            )}
            <div className="status-row">
              <span className="status-label">Party</span>
              <span className="status-value">{party.name}</span>
            </div>
            <div className="status-row">
              <span className="status-label">Member</span>
              <span className="status-value">{memberName}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
